import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    indexFinalConceptsByTeacherType,
    clssInfo,
    GetMatter,
    GetMatterDetails,
    getIstQuarter,
    FinalConceptsEdit
} from '../../Api'

import {
    Container,
    ContainerDivs,
    Header,
    Title,
    NewActivityButton,
    Toolbar,
    ToolbarActions,
    SearchInput,
    ToggleAllButton,
    EmptyState,
    MatterCard,
    MatterHeader,
    MatterTitle,
    Badge,
    ChevronWrap,
    MatterBody,
    ActivityMeta,
    ActivityActions,
    Tag,
    ConceptTag,
    TagSpan,
    EditButton,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    FormCard,
    FormHeader,
    FormTitle,
    FormSubtitle,
    FormGroup,
    FormLabel,
    FormSelect,
    FormError,
    FormActions,
    SaveButton,
    CancelButton,
    Overlay,
    SkeletonCard,
    SkeletonLine
} from './style'

// ── helpers fora do componente ──────────────────────────────────────
const extractId = (v) => {
    if (!v) return null
    if (typeof v === 'string') {
        const t = v.trim()
        if ((t.startsWith('"') && t.endsWith('"')) ||
            (t.startsWith('[') && t.endsWith(']')) ||
            (t.startsWith('{') && t.endsWith('}'))) {
            try { v = JSON.parse(t) } catch { /* mantém v */ }
        }
    }
    if (Array.isArray(v)) v = v[0]
    if (v && typeof v === 'object') v = v._id ?? v.id ?? null
    const s = String(v ?? '').trim()
    return /^[a-fA-F0-9]{24}$/.test(s) ? s : null
}

const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

// nome do aluno (campo populado ou não)
const studentName = (c) =>
    (c.id_student && typeof c.id_student === 'object' ? c.id_student.name : null)
    || c.studentName
    || 'Aluno'

// rótulo do conceito ("-"/vazio => "Sem conceito")
const conceptLabel = (v) =>
    v === '-' || v == null || v === '' ? 'Sem conceito' : v

/* ── Linha de conceito final (memoizada) ─────────────────────────
   Botões só ficam ativos quando o período final está aberto e o
   item já está confirmado pelo servidor (sem id temporário).      */
const FinalConceptRow = memo(function FinalConceptRow({ concept, canEdit, onEdit, busy }) {
    const nome = studentName(concept)
    const valor = concept.studentGrade
    const isTemp = String(concept._id).startsWith('temp-')
    const disabled = !canEdit || isTemp || busy

    return (
        <TagSpan>
            <Tag>{nome}</Tag>

            <ActivityActions>
                <ConceptTag grade={valor}>{conceptLabel(valor)}</ConceptTag>

                <EditButton
                    disabled={disabled}
                    onClick={() => !disabled && onEdit(concept)}
                    title={canEdit ? 'Editar conceito final' : 'Período fechado'}
                >
                    {canEdit ? 'Editar' : 'Período Fechado'}
                </EditButton>
            </ActivityActions>
        </TagSpan>
    )
})

const FinalConcepts = () => {
    const navigate = useNavigate()

    // ── listagem ─────────────────────────────────────────────────────
    const [concepts, setConcepts] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [busca, setBusca] = useState('')
    const [debouncedBusca, setDebouncedBusca] = useState('')
    const [openMatters, setOpenMatters] = useState({})

    // ── contexto ─────────────────────────────────────────────────────
    const [year, setYear] = useState('')
    const [open, setOpen] = useState('aberto')          // status do último período
    const [assessmentRegime, setAssessmentRegime] = useState('')

    // ── edição inline ────────────────────────────────────────────────
    const [editingConcept, setEditingConcept] = useState(null)
    const [editGrade, setEditGrade] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)
    const [editError, setEditError] = useState('')

    // ── matérias + modal de novo conceito final ──────────────────────
    const [matters, setMatters] = useState([])
    const [showForm, setShowForm] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState('')
    const [selectedMatter, setSelectedMatter] = useState('')

    const mountedRef = useRef(true)
    const [reloadKey, setReloadKey] = useState(0)

    const isFinalOpen = open === 'aberto'

    // ── debounce da busca ────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300)
        return () => clearTimeout(t)
    }, [busca])

    // ── CARGA PRINCIPAL ──────────────────────────────────────────────
    useEffect(() => {
        mountedRef.current = true
        let cancelled = false
        const safeSet = (fn) => { if (!cancelled && mountedRef.current) fn() }

            ; (async () => {
                safeSet(() => { setLoading(true); setLoadError('') })

                const id_class = sessionStorage.getItem('class-info')
                const id_emp = localStorage.getItem('Id_employee')
                const regimeFromStorage = sessionStorage.getItem('assessmentRegime') || ''

                const myId = extractId(id_emp)
                const physicalId = extractId(safeParse(sessionStorage.getItem('physicalEducationTeacher')))
                const amIPhysical = !!physicalId && myId === physicalId
                const teacher_type = amIPhysical ? 'ED_FISICA' : 'REGENTE'

                // validação mínima
                if (!id_class) {
                    safeSet(() => {
                        setLoadError('Não foi possível carregar os dados da turma. Volte e selecione a turma novamente.')
                        setLoading(false)
                    })
                    return
                }

                try {
                    const idSchool = safeParse(sessionStorage.getItem('id-school'))

                    const resClass = await clssInfo(id_class)
                    if (cancelled || !mountedRef.current) return

                    const turma = resClass?.data?.data?.[0]
                    const yearValue = turma?.year

                    // ── regime definido pelo 1º bimestre (igual à página de conceitos) ──
                    // lê assessmentRegime do primeiro registro; se não houver, usa o sessionStorage.
                    let regime = regimeFromStorage
                    try {
                        const resQuarter = await getIstQuarter(yearValue, idSchool)
                        if (cancelled || !mountedRef.current) return
                        const firstQuarter = resQuarter?.data?.data?.find(r => r)
                        if (firstQuarter?.assessmentRegime) {
                            regime = firstQuarter.assessmentRegime
                        }
                    } catch (e) {
                        console.warn('Não foi possível obter o regime pelo 1º bimestre, usando sessionStorage.', e)
                    }

                    // ── bloqueio do período final ──────────────────────
                    // trimestral => 3º; bimestral => 4º; campo conforme tipo de professor.
                    // 'fechado' = trancado (bloqueia edição/exclusão).
                    const periodoFinal = regime === 'TRIMESTRAL' ? '3º BIMESTRE' : '4º BIMESTRE'
                    const st = turma?.dailyStatus?.[periodoFinal]
                    if (st) {
                        safeSet(() => setOpen(amIPhysical ? st.physicalEducationTeacher : st.regentTeacher))
                    }

                    // lista de conceitos finais + matérias da escola, em paralelo
                    const [resConcepts, resMatter] = await Promise.all([
                        indexFinalConceptsByTeacherType({
                            id_class,
                            year: yearValue,
                            teacher_type
                        }),
                        GetMatter(idSchool)
                    ])
                    if (cancelled || !mountedRef.current) return

                    const allMatters = resMatter?.data?.data || []
                    const filteredMatters = allMatters.filter(m =>
                        amIPhysical ? m.name === 'EDUCAÇÃO FÍSICA' : m.name !== 'EDUCAÇÃO FÍSICA'
                    )

                    safeSet(() => {
                        setConcepts(resConcepts?.data?.data || [])
                        setMatters(filteredMatters)
                        setYear(yearValue || '')
                        setAssessmentRegime(regime)
                        setLoading(false)
                        setLoadError('')
                    })
                } catch (err) {
                    console.error('Erro ao carregar conceitos finais:', err)
                    safeSet(() => {
                        setLoadError('Erro ao carregar os dados. Verifique sua conexão e tente novamente.')
                        setLoading(false)
                    })
                }
            })()

        return () => { cancelled = true; mountedRef.current = false }
    }, [reloadKey])

    // scroll lock + ESC quando há modal aberto
    useEffect(() => {
        const anyModal = !!editingConcept || showForm
        if (!anyModal) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e) => {
            if (e.key !== 'Escape') return
            if (!savingEdit && !submitting) {
                setEditingConcept(null); setEditError('')
                setShowForm(false); setFormError('')
            }
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = prev
            window.removeEventListener('keydown', onKey)
        }
    }, [editingConcept, showForm, savingEdit, submitting])

    // recarrega lista em memória (sem reload de página)
    const refreshConcepts = useCallback(async () => {
        try {
            const id_class = sessionStorage.getItem('class-info')
            const id_emp = localStorage.getItem('Id_employee')
            const myId = extractId(id_emp)
            const physicalId = extractId(safeParse(sessionStorage.getItem('physicalEducationTeacher')))
            const teacher_type = (!!physicalId && myId === physicalId) ? 'ED_FISICA' : 'REGENTE'

            const res = await indexFinalConceptsByTeacherType({ id_class, year, teacher_type })
            if (!mountedRef.current) return
            setConcepts(res?.data?.data || [])
        } catch (err) {
            console.error(err)
        }
    }, [year])

    // ── agrupamento por matéria ──────────────────────────────────────
    const grouped = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const groups = {}

        for (const c of concepts) {
            if (q) {
                const haystack = [studentName(c), c.studentGrade, c.id_matter?.name]
                    .filter(Boolean).join(' ').toLowerCase()
                if (!haystack.includes(q)) continue
            }
            const matterId = c.id_matter?._id || 'sem_materia'
            const matterName = c.id_matter?.name || 'Sem matéria'
            if (!groups[matterId]) {
                groups[matterId] = {
                    id: matterId,
                    name: matterName,
                    concepts: [],
                    counts: { A: 0, B: 0, C: 0, D: 0 }
                }
            }
            const m = groups[matterId]
            m.concepts.push(c)
            if (m.counts[c.studentGrade] !== undefined) m.counts[c.studentGrade]++
        }

        return Object.values(groups)
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
            .map(m => ({
                ...m,
                concepts: m.concepts
                    .slice()
                    .sort((a, b) => studentName(a).localeCompare(studentName(b), 'pt-BR'))
            }))
    }, [concepts, debouncedBusca])

    const totalConceitos = useMemo(
        () => grouped.reduce((acc, m) => acc + m.concepts.length, 0),
        [grouped]
    )

    // ── toggles ──────────────────────────────────────────────────────
    const toggleMatter = useCallback((id) =>
        setOpenMatters(prev => ({ ...prev, [id]: !prev[id] })), [])

    const allExpanded = useMemo(() => {
        if (grouped.length === 0) return false
        return grouped.every(m => openMatters[m.id])
    }, [grouped, openMatters])

    const toggleAll = useCallback(() => {
        if (allExpanded) {
            setOpenMatters({})
        } else {
            const mts = {}
            for (const m of grouped) mts[m.id] = true
            setOpenMatters(mts)
        }
    }, [allExpanded, grouped])

    // ── edição inline ────────────────────────────────────────────────
    const openEdit = useCallback((concept) => {
        if (!isFinalOpen) return
        setEditError('')
        setEditingConcept(concept)
        setEditGrade(concept.studentGrade ?? '')
    }, [isFinalOpen])

    const closeEdit = () => {
        if (savingEdit) return
        setEditingConcept(null)
        setEditError('')
    }

    const saveEdit = async () => {
        if (savingEdit || !editingConcept) return
        if (!isFinalOpen) {
            setEditError('O período final está fechado. Contate o Supervisor.')
            return
        }
        if (!editGrade) {
            setEditError('Selecione um conceito ou marque "Não adicionar nota".')
            return
        }

        const editedId = editingConcept._id
        const novo = editGrade
        const prev = concepts

        // atualização otimista
        setConcepts(list => list.map(c =>
            c._id === editedId ? { ...c, studentGrade: novo } : c
        ))
        setEditingConcept(null)
        setSavingEdit(true)
        setEditError('')

        try {
            await FinalConceptsEdit(editedId, novo)
            if (!mountedRef.current) return
            refreshConcepts()
        } catch (err) {
            console.error(err)
            if (mountedRef.current) {
                setConcepts(prev) // rollback
                setEditError('Erro ao salvar. O conceito foi mantido como estava.')
            }
        } finally {
            if (mountedRef.current) setSavingEdit(false)
        }
    }

    // ── novo conceito final: seleciona matéria e vai para /final-concepts ──
    const openCreate = () => {
        setFormError('')
        setSelectedMatter('')
        setShowForm(true)
    }

    const closeForm = () => {
        if (submitting) return
        setShowForm(false)
        setFormError('')
    }

    // Salva a matéria no sessionStorage (padrão que /final-concepts já lê)
    // e navega. Bloqueia se o período final estiver fechado.
    const handleSubmit = async () => {
        if (submitting) return
        if (!isFinalOpen) {
            setFormError('O período final está fechado. Contate o Supervisor.')
            return
        }
        if (!selectedMatter) {
            setFormError('Selecione uma matéria.')
            return
        }

        setSubmitting(true)
        setFormError('')
        try {
            const Matter = await GetMatterDetails(selectedMatter)
            if (Matter) {
                sessionStorage.setItem('nameMatter', Matter.data.name)
            }
            sessionStorage.setItem('Selectmatter', selectedMatter)
            sessionStorage.setItem('yearGrade', year)

            closeForm()
            navigate('/final-concepts')
        } catch (err) {
            console.error(err)
            setFormError('Erro ao abrir o cadastro. Tente novamente.')
            setSubmitting(false)
        }
    }

    const periodLabel = assessmentRegime === 'TRIMESTRAL' ? '3º Trimestre' : '4º Bimestre'

    // ── RENDER ───────────────────────────────────────────────────────
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <Title>Conceitos Finais</Title>
                    </Header>
                    {[1, 2, 3].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="40%" $h="18px" />
                            <SkeletonLine $w="80%" />
                            <SkeletonLine $w="60%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    if (loadError) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <Title>Conceitos Finais</Title>
                    </Header>
                    <EmptyState>{loadError}</EmptyState>
                    <ToggleAllButton onClick={() => setReloadKey(k => k + 1)}>
                        Tentar novamente
                    </ToggleAllButton>
                </ContainerDivs>
            </Container>
        )
    }

    const editStudentName = editingConcept ? studentName(editingConcept) : ''

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <Title>Conceitos Finais</Title>
                    <NewActivityButton
                        onClick={openCreate}
                        title="Adicionar conceitos finais"
                    >
                        + Novo Conceito Final
                    </NewActivityButton>
                </Header>

                <Toolbar>
                    <SearchInput
                        type="text"
                        placeholder="Buscar por aluno, conceito ou matéria..."
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        aria-label="Buscar conceitos finais"
                    />
                    <ToolbarActions>
                        <Badge>
                            {totalConceitos} {totalConceitos === 1 ? 'conceito' : 'conceitos'}
                        </Badge>
                        {grouped.length > 0 && (
                            <ToggleAllButton onClick={toggleAll}>
                                {allExpanded ? 'Colapsar tudo' : 'Expandir tudo'}
                            </ToggleAllButton>
                        )}
                    </ToolbarActions>
                </Toolbar>

                {!isFinalOpen && (
                    <EmptyState>
                        O {periodLabel} está fechado. Os conceitos finais não podem ser editados. Contate o Diretor ou Supervisor.
                    </EmptyState>
                )}

                {grouped.length === 0 ? (
                    <EmptyState>
                        {debouncedBusca
                            ? `Nenhum conceito final encontrado para "${debouncedBusca}".`
                            : 'Nenhum conceito final encontrado.'}
                    </EmptyState>
                ) : (
                    grouped.map((m) => {
                        const matterOpen = !!openMatters[m.id]
                        return (
                            <MatterCard key={m.id}>
                                <MatterHeader
                                    $open={matterOpen}
                                    onClick={() => toggleMatter(m.id)}
                                    aria-expanded={matterOpen}
                                >
                                    <MatterTitle>
                                        {m.name}
                                        <Badge>{m.concepts.length}</Badge>
                                    </MatterTitle>
                                    <ActivityMeta>
                                        <Tag $variant="tipo">A: {m.counts.A}</Tag>
                                        <Tag $variant="media">B: {m.counts.B}</Tag>
                                        <Tag $variant="valor">C: {m.counts.C}</Tag>
                                        <Tag>D: {m.counts.D}</Tag>
                                        <ChevronWrap $open={matterOpen}>▼</ChevronWrap>
                                    </ActivityMeta>
                                </MatterHeader>

                                {matterOpen && (
                                    <MatterBody>
                                        {m.concepts.map((c) => (
                                            <FinalConceptRow
                                                key={c._id}
                                                concept={c}
                                                canEdit={isFinalOpen}
                                                onEdit={openEdit}
                                                busy={savingEdit}
                                            />
                                        ))}
                                    </MatterBody>
                                )}
                            </MatterCard>
                        )
                    })
                )}

                <ToGoBack onClick={() => navigate(-1)} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>

            {/* MODAL DE EDIÇÃO */}
            {editingConcept && (
                <Overlay onClick={closeEdit}>
                    <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <FormHeader>
                            <FormTitle>Editar Conceito Final</FormTitle>
                            <FormSubtitle>
                                {editStudentName}
                                {editingConcept.id_matter?.name ? ` · ${editingConcept.id_matter.name}` : ''}
                            </FormSubtitle>
                        </FormHeader>

                        <FormGroup>
                            <FormLabel>Conceito</FormLabel>
                            <FormSelect
                                value={editGrade}
                                onChange={(e) => { setEditGrade(e.target.value); setEditError('') }}
                                disabled={editGrade === '-'}
                            >
                                <option value="">Selecione</option>
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                            </FormSelect>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>
                                <input
                                    type="checkbox"
                                    checked={editGrade === '-'}
                                    onChange={(e) => { setEditGrade(e.target.checked ? '-' : ''); setEditError('') }}
                                    style={{ marginRight: '6px' }}
                                />
                                Não adicionar nota para este aluno
                            </FormLabel>
                        </FormGroup>

                        {editError && <FormError>{editError}</FormError>}

                        <FormActions>
                            <CancelButton onClick={closeEdit} disabled={savingEdit}>
                                Cancelar
                            </CancelButton>
                            <SaveButton onClick={saveEdit} disabled={savingEdit}>
                                {savingEdit ? 'Atualizando...' : 'Atualizar'}
                            </SaveButton>
                        </FormActions>
                    </FormCard>
                </Overlay>
            )}

            {/* MODAL DE NOVO CONCEITO FINAL (seleciona matéria → /final-concepts) */}
            {showForm && (
                <Overlay onClick={closeForm}>
                    <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <FormHeader>
                            <FormTitle>Adicionar Conceito Final</FormTitle>
                            <FormSubtitle>
                                Selecione a matéria para lançar os conceitos finais
                            </FormSubtitle>
                        </FormHeader>

                        <FormGroup>
                            <FormLabel>Matéria</FormLabel>
                            <FormSelect
                                value={selectedMatter}
                                onChange={(e) => { setSelectedMatter(e.target.value); setFormError('') }}
                            >
                                <option value="">Selecione</option>
                                {matters.map(m => (
                                    <option key={m._id} value={m._id}>{m.name}</option>
                                ))}
                            </FormSelect>
                        </FormGroup>

                        {formError && <FormError>{formError}</FormError>}

                        <FormActions>
                            <CancelButton onClick={closeForm} disabled={submitting}>
                                Cancelar
                            </CancelButton>
                            <SaveButton onClick={handleSubmit} disabled={submitting}>
                                {submitting ? 'Abrindo...' : 'Continuar'}
                            </SaveButton>
                        </FormActions>
                    </FormCard>
                </Overlay>
            )}

        </Container>
    )
}

export default FinalConcepts
